// New generation job (contracts/studio.md §3–4): a type picker grouped by category with capability
// badges; procedural or Claude; count within studio.maxPerJob; a difficulty range; for Claude, a model
// from studio.prices (default studio.defaultModel), an optional theme and a cost estimate against the
// budget. The API's refusals (ai_disabled, ai_unavailable, budget_exceeded, engine_unavailable) are
// shown up front where the panel can see them coming, and in plain words when the API answers.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useConfig } from "../../api/queries";
import { useCreateJob, useStudioTypes, useStudioUsage, type NewJob } from "../../api/studio";
import type { StudioType } from "../../api/studioSchemas";
import { useAuth, useCan } from "../../auth/AuthContext";
import { Button, Card, CategoryTag, Chip, EmptyState, Field, Loading, PageHeader, Segmented, Select, TextInput } from "../../components/ui";
import { useLevelGenerator } from "../../api/levelPacks";
import { LevelPackForm } from "../levelpacks/LevelPackForm";
import l from "../levelpacks/levelpacks.module.css";
import { fmtCompact, typeSlug } from "../../lib/format";
import { DIFFICULTY_PRESETS, estimateAiCost, fmtUsd, groupTypes, type Price } from "../../lib/studio";
import { BackLink, CapabilityBadges, Callout, RoleNote, StudioError } from "./components";
import s from "./studio.module.css";

type Kind = "procedural" | "ai";

const num = (v: string) => (v.trim() === "" ? NaN : Number(v));

export function StudioNewJobPage() {
  const [params, setParams] = useSearchParams();
  const levelPack = params.get("kind") === "levelpack";
  const types = useStudioTypes();
  const header = (
    <PageHeader
      title="New job"
      meta={
        <>
          <BackLink to="/studio">Studio</BackLink>
          <span>
            {levelPack
              ? "A level pack job builds the next 50-level packs of a track with iqcontent's pack builder, as drafts to review and publish."
              : "Procedural jobs use the iqcontent generators; Claude jobs draft from a versioned prompt. Both are verified by the same code."}
          </span>
        </>
      }
      actions={
        <>
          <RoleNote />
          <Segmented<"puzzles" | "levelpack">
            label="Job"
            value={levelPack ? "levelpack" : "puzzles"}
            onChange={(v) => setParams(v === "levelpack" ? { kind: "levelpack" } : {}, { replace: true })}
            options={[
              { value: "puzzles", label: "Puzzles" },
              { value: "levelpack", label: "Level pack" },
            ]}
          />
        </>
      }
    />
  );
  if (levelPack) {
    return (
      <>
        {header}
        <LevelPackJobCard initialTrack={params.get("track")} />
      </>
    );
  }
  if (types.isPending) {
    return (
      <>
        {header}
        <Loading rows={10} />
      </>
    );
  }
  if (types.isError) {
    return (
      <>
        {header}
        <StudioError error={types.error} onRetry={() => void types.refetch()} title="Can't load the puzzle types" />
      </>
    );
  }
  return (
    <>
      {header}
      <NewJobForm types={types.data.types} ai={types.data.ai} initialType={params.get("type")} initialKind={params.get("kind") === "ai" ? "ai" : null} />
    </>
  );
}

/** Studio → New job → Level pack: the same form as Levels → Generate next pack. */
function LevelPackJobCard({ initialTrack }: { initialTrack: string | null }) {
  const navigate = useNavigate();
  const generator = useLevelGenerator();
  return (
    <div className={s.newGrid}>
      <Card title="Level pack" className={s.formCard}>
        {generator.isPending ? (
          <Loading rows={6} />
        ) : generator.isError ? (
          <StudioError error={generator.error} onRetry={() => void generator.refetch()} title="Can't reach the engine" />
        ) : (
          <LevelPackForm tracks={generator.data.tracks} initialTrack={initialTrack} onQueued={(id) => navigate(`/studio/jobs/${id}`)} />
        )}
      </Card>
      <Card title="How it works">
        <ul className={l.dialogList}>
          <li>The engine builds each pack with iqcontent&rsquo;s own pack builder: the track&rsquo;s schedule, its type mix, the difficulty ramp with reliefs and gates, and no repeats inside the pack.</li>
          <li>Every level passes its type&rsquo;s solver (exactly one answer) and carries its <span className="mono">check</span> digest; answers never leave the server.</li>
          <li>With the schedule and canonical seeds, a pack is byte-identical to what <span className="mono">python -m iqcontent extend</span> writes.</li>
          <li>Packs land as drafts at the track&rsquo;s next free index. Review them in Levels, re-generate single levels, then publish in order. Published packs are immutable.</li>
        </ul>
      </Card>
    </div>
  );
}

function NewJobForm({
  types,
  ai,
  initialType,
  initialKind,
}: {
  types: StudioType[];
  ai: { enabled: boolean; keyConfigured: boolean; defaultModel: string | null };
  initialType: string | null;
  initialKind: Kind | null;
}) {
  const navigate = useNavigate();
  const canEdit = useCan("studio.edit");
  const isAdmin = useCan("studio.killSwitch");
  const { admin } = useAuth();
  const config = useConfig();
  const usage = useStudioUsage();
  const create = useCreateJob();

  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<string>(() => (initialType && types.some((t) => t.type === initialType) ? initialType : ""));
  const type = types.find((t) => t.type === selected) ?? null;
  const [kindPick, setKindPick] = useState<Kind | null>(initialKind);
  const kind: Kind = type ? (kindPick === "ai" && type.aiSupported) || !type.procedural ? "ai" : "procedural" : (kindPick ?? "procedural");
  const [count, setCount] = useState("10");
  const [min, setMin] = useState("0");
  const [max, setMax] = useState("1");
  const [model, setModel] = useState("");
  const [theme, setTheme] = useState("");

  const values = config.data?.values ?? {};
  const maxPerJob = (values["studio.maxPerJob"] as { ai?: number; procedural?: number } | undefined) ?? { ai: 25, procedural: 200 };
  const limit = (kind === "ai" ? maxPerJob.ai : maxPerJob.procedural) ?? (kind === "ai" ? 25 : 200);
  const prices = (values["studio.prices"] as Record<string, Price> | undefined) ?? {};
  const defaultModel = typeof values["studio.defaultModel"] === "string" ? (values["studio.defaultModel"] as string) : (ai.defaultModel ?? "");
  const models = Object.keys(prices).sort();
  const chosenModel = model || defaultModel;

  const n = num(count);
  const lo = num(min);
  const hi = num(max);
  const countError = !Number.isInteger(n) || n < 1 ? "A whole number, 1 or more." : n > limit ? `At most ${limit} per ${kind === "ai" ? "Claude" : "procedural"} job (studio.maxPerJob).` : null;
  const rangeError = !(lo >= 0 && lo <= 1 && hi >= 0 && hi <= 1) ? "Scores run from 0 to 1." : lo > hi ? "Min is above max." : null;
  const themeError = theme.length > 200 ? `${theme.length} of 200 characters.` : null;
  const estimate = kind === "ai" && !countError ? estimateAiCost(n, prices[chosenModel]) : null;
  const remaining = usage.data?.remainingUsd ?? null;

  // A type picked by link (?type=) may sit far down the list: scroll the picker (not the page) to it.
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const picker = pickerRef.current;
    const el = picker?.querySelector<HTMLElement>('[aria-checked="true"]');
    if (picker && el) picker.scrollTop = Math.max(0, el.offsetTop - picker.offsetTop - 60);
    // Only on the first render: later picks are made by clicking, in view.
  }, []);

  const groups = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return groupTypes(types.filter((t) => !f || t.type.includes(f) || t.category.includes(f)));
  }, [types, filter]);

  // What the API would refuse, as far as the panel can see it.
  const blockers: { code: string; title: string; body: ReactNode; tone: "warning" | "danger" }[] = [];
  if (kind === "ai") {
    if (!ai.enabled) {
      blockers.push({
        code: "ai_disabled",
        tone: "warning",
        title: "The AI kill switch is off",
        body: isAdmin ? (
          <p>
            Claude jobs are refused until <code>studio.aiEnabled</code> is on. Turn it on from the <Link to="/studio">Studio overview</Link>.
          </p>
        ) : (
          <p>Claude jobs are refused until an admin turns on <code>studio.aiEnabled</code>.</p>
        ),
      });
    }
    if (!ai.keyConfigured) {
      blockers.push({ code: "ai_unavailable", tone: "warning", title: "Claude isn't available on the engine", body: <p>The engine has no ANTHROPIC_API_KEY. Procedural jobs still work.</p> });
    }
    if (remaining !== null && remaining <= 0) {
      blockers.push({ code: "budget_exceeded", tone: "danger", title: "This month's Claude budget is spent", body: <p>An admin can raise <code>studio.monthlyBudgetUsd</code> in Config, or wait for the next UTC month.</p> });
    }
  }
  const hardBlocked = blockers.length > 0;
  const invalid = Boolean(!type || countError || rangeError || themeError || (kind === "ai" && !chosenModel));

  const submit = () => {
    if (!type || invalid) return;
    const body: NewJob = { kind, type: type.type, count: n, difficulty: { min: lo, max: hi } };
    if (kind === "ai") {
      if (model) body.model = model;
      if (theme.trim()) body.theme = theme.trim();
    }
    create.mutate(body, { onSuccess: ({ job }) => navigate(`/studio/jobs/${job.id}`) });
  };

  return (
    <div className={s.newGrid}>
      <Card title="Puzzle type" actions={<span className="monoLabel">{types.length} types · engine catalogue</span>}>
        <div className={s.pickerSearch}>
          <TextInput data-search placeholder="Filter types  ( / )" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter types" style={{ width: "100%" }} />
        </div>
        <div className={s.picker} role="radiogroup" aria-label="Puzzle type" data-testid="type-picker" ref={pickerRef}>
          {groups.length === 0 ? <EmptyState title="No type matches" /> : null}
          {groups.map(([category, ts]) => (
            <section key={category} className={s.group} aria-label={category}>
              <div className={s.groupHead}>
                <CategoryTag category={category} />
                <span className="monoLabel">{ts.length}</span>
              </div>
              {ts.map((t) => (
                <button
                  key={t.type}
                  type="button"
                  role="radio"
                  aria-checked={t.type === selected}
                  className={s.typeOption}
                  data-type={t.type}
                  onClick={() => {
                    setSelected(t.type);
                    create.reset();
                  }}
                >
                  <span className={s.typeName}>
                    <strong>{typeSlug(t.type)}</strong>
                    <span>{t.type}</span>
                  </span>
                  <CapabilityBadges type={t} />
                </button>
              ))}
            </section>
          ))}
        </div>
      </Card>

      <Card title="Job" className={s.formCard}>
        {!type ? (
          <EmptyState title="Pick a puzzle type">Its badges say what it can do: a solver (or a person) checks every puzzle; procedural types have a generator; AI types have a prompt.</EmptyState>
        ) : (
          <form
            className={s.form}
            data-testid="job-form"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <div className={s.chosen}>
              <span className={s.chosenTitle}>
                {type.type}
                <CapabilityBadges type={type} />
              </span>
              {type.howTo ? <p className={s.howTo}>{type.howTo}</p> : null}
            </div>

            <div className={s.fieldCol}>
              <span className="monoLabel">Kind</span>
              <div className={s.kinds} role="radiogroup" aria-label="Kind">
                <button type="button" role="radio" className={s.kind} aria-checked={kind === "procedural"} disabled={!type.procedural} onClick={() => setKindPick("procedural")}>
                  <strong>Procedural</strong>
                  {type.procedural ? "iqcontent generator · solver-verified · no tokens" : "No generator for this type"}
                </button>
                <button type="button" role="radio" className={s.kind} aria-checked={kind === "ai"} disabled={!type.aiSupported} onClick={() => setKindPick("ai")}>
                  <strong>Claude</strong>
                  {type.aiSupported ? `${type.promptVersion ?? "prompt"} · ${type.hasSolver ? "solver-checked" : "needs review"} · costs tokens` : "No prompt for this type"}
                </button>
              </div>
            </div>

            <div className={s.row}>
              <Field label="Count" hint={countError ? <span className={s.locked}>{countError}</span> : `1–${limit} candidates (studio.maxPerJob)`}>
                <TextInput type="number" min={1} max={limit} value={count} onChange={(e) => setCount(e.target.value)} aria-label="Count" aria-invalid={Boolean(countError)} />
              </Field>
              {kind === "ai" ? (
                <Field label="Model" hint={prices[chosenModel] ? `$${prices[chosenModel]!.input} in · $${prices[chosenModel]!.output} out per MTok` : "Needs a row in studio.prices"}>
                  <Select value={model} onChange={(e) => setModel(e.target.value)} aria-label="Model">
                    <option value="">{defaultModel ? `${defaultModel} (default)` : "Default"}</option>
                    {models
                      .filter((m) => m !== defaultModel)
                      .map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                  </Select>
                </Field>
              ) : (
                <div />
              )}
            </div>

            <div className={s.fieldCol}>
              <span className="monoLabel">Difficulty</span>
              <div className={s.presets}>
                {DIFFICULTY_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={s.preset}
                    aria-pressed={lo === p.min && hi === p.max}
                    onClick={() => {
                      setMin(String(p.min));
                      setMax(String(p.max));
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className={s.row}>
                <Field label="Min score">
                  <TextInput type="number" min={0} max={1} step={0.05} value={min} onChange={(e) => setMin(e.target.value)} aria-label="Min difficulty" />
                </Field>
                <Field label="Max score">
                  <TextInput type="number" min={0} max={1} step={0.05} value={max} onChange={(e) => setMax(e.target.value)} aria-label="Max difficulty" />
                </Field>
              </div>
              <div className={s.rangeViz} aria-hidden>
                {!rangeError ? <span className={s.rangeBand} style={{ left: `${lo * 100}%`, width: `${Math.max(1, (hi - lo) * 100)}%` }} /> : null}
              </div>
              <div className={s.rangeLabels}>
                <span>easy</span>
                <span>0.4 medium</span>
                <span>0.65 hard</span>
                <span>1</span>
              </div>
              {rangeError ? <span className={s.locked}>{rangeError}</span> : null}
            </div>

            {kind === "ai" ? (
              <>
                <Field label="Theme (optional)" hint={themeError ?? "Claude uses it where it fits, e.g. kitchen, space, football."}>
                  <TextInput value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="kitchen" aria-label="Theme" maxLength={240} />
                </Field>
                {estimate ? (
                  <div className={s.estimate} data-testid="estimate">
                    <span>
                      At most <strong>{fmtUsd(estimate.usd)}</strong> · ≈ {fmtCompact(estimate.input)} in + {fmtCompact(estimate.output)} out tokens
                    </span>
                    <span>
                      Budget left <strong>{fmtUsd(remaining)}</strong>
                    </span>
                  </div>
                ) : null}
                {estimate && remaining !== null && remaining > 0 && estimate.usd > remaining ? (
                  <Callout tone="warning" title="This job may stop early">
                    <p>Each batch is sized to fit the budget; once nothing fits, the job fails with budget_exceeded.</p>
                  </Callout>
                ) : null}
              </>
            ) : null}

            {blockers.length ? (
              <div className={s.callouts} data-testid="job-blockers">
                {blockers.map((b) => (
                  <Callout key={b.code} tone={b.tone} title={b.title} code={b.code}>
                    {b.body}
                  </Callout>
                ))}
              </div>
            ) : null}
            <StudioError error={create.error} />

            <div className={s.submitRow}>
              {canEdit ? (
                <Button type="submit" variant="primary" busy={create.isPending} disabled={invalid || hardBlocked}>
                  Queue job
                </Button>
              ) : (
                <Chip tone="muted">queueing jobs needs the editor role (you are {admin?.role})</Chip>
              )}
              <span className={s.summary}>
                {kind === "ai" ? "Claude" : "Procedural"} · {Number.isFinite(n) ? n : "?"} × {typeSlug(type.type)} · {Number.isFinite(lo) ? lo.toFixed(2) : "?"}–{Number.isFinite(hi) ? hi.toFixed(2) : "?"}
              </span>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
